/**
 * gui-react adapter for the portable brewauth admin app-access client
 * (`./brewauthAdmin`).
 *
 * Wires the app's OIDC access-token getter into
 * `createBrewauthAdminClient` so callers (App Access tab, admin page —
 * Tasks 5-6) can import a ready-to-use instance:
 * `import { brewauthAdmin } from '@/api/brewauthAdminClient'`.
 *
 * `baseUrl: ''` resolves requests to the same-origin absolute path
 * `/api/brewauth/admin/app-access/*`, which the revproxy routes to
 * brewauth.
 */
import {
  createBrewauthAdminClient,
  type BrewauthAdminClient,
  type BrewauthAdminConfig,
} from './brewauthAdmin';
import { getAccessToken, auth } from '@/auth/reauthClient';
import { withAttentionInvalidation } from '@/attention/withAttentionInvalidation';

export * from './brewauthAdmin';

/**
 * How gui-react reaches brewauth: same origin, the app's OIDC token, and the
 * app's 401 ladder. Exported so every brewauth client built from a config
 * (this admin client, `./brewauthGroups`) shares ONE copy of the token and
 * re-auth logic instead of each carrying its own.
 */
export const brewauthClientConfig: BrewauthAdminConfig = {
  baseUrl: '',
  getToken: getAccessToken,
  // Mirror the axios 401 ladder for these raw-fetch admin calls so an expired
  // session surfaces the same re-auth popup as the rest of the app: a
  // server-revoked token goes straight to interactive re-auth (a silent
  // refresh would just fail); otherwise try a silent refresh, then fall back
  // to the popup. Returns true (session recovered → retry) or false (cancelled).
  onUnauthorized: async (staleToken, revoked) => {
    const refreshed = revoked ? false : await auth.silentRefresh(staleToken);
    if (refreshed) return true;
    try {
      await auth.reauth.requireInteractiveReauth();
      return true;
    } catch {
      return false;
    }
  },
};

/**
 * Mutations that change what an attention badge counts (spec §7.3). A
 * successful call refreshes the matching sources straight away.
 */
const ADMIN_MUTATION_TAGS: Partial<Record<keyof BrewauthAdminClient, string[]>> = {
  grant: ['app-access'],
  revoke: ['app-access'],
  deny: ['app-access'],
  setRole: ['app-access'],
  declineRoleRequest: ['app-access'],
  bulkGrant: ['app-access'],
  activateProfile: ['app-access'],
  createDeletionJob: ['app-access', 'deletion-jobs'],
  createMergeJob: ['app-access', 'deletion-jobs'],
  retryDeletionJob: ['deletion-jobs'],
  abortDeletionJob: ['deletion-jobs'],
};

export const brewauthAdmin = withAttentionInvalidation(
  createBrewauthAdminClient(brewauthClientConfig),
  ADMIN_MUTATION_TAGS,
);

/**
 * For the background attention poll only. Bearer token like the main client,
 * but a 401 gets one silent refresh and never the interactive re-auth ladder
 * (spec A4: a poll must not open a modal or keep the session alive).
 */
export const brewauthAdminBackground = createBrewauthAdminClient({
  ...brewauthClientConfig,
  onUnauthorized: (stale, revoked) =>
    revoked ? Promise.resolve(false) : auth.silentRefresh(stale),
});
